import {
  type AiBudgetView,
  type CreateKnowledgeInput,
  type KnowledgeImpact,
  type KnowledgeStatus,
  type KnowledgeTag,
  type OrgKnowledgeView,
  type UpdateKnowledgeInput,
  type DemoActivityStatus,
  type DemoMaintenanceStatus,
  type DemoProjectOverrideInput,
  type DemoScenarioView,
  type AiProviderKeyInput,
  type AiProviderKeyView,
  type AiKeyTestResult,
  type FileCapabilities,
  type GoogleOauthConfigInput,
  type GoogleOauthStatus,
  type StorageStatus,
  type SystemSettingKey,
  type SystemSettingView,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';

export interface AdminOverview {
  storage: StorageStatus;
  ai: { configured: boolean };
  settings: SystemSettingView[];
  uploadsDisabledReason: string | null;
}

export const adminApi = {
  overview: () => apiFetch<AdminOverview>('/admin/overview'),

  setSetting: (key: SystemSettingKey, value: unknown) =>
    apiFetch<SystemSettingView[]>('/admin/settings', { method: 'POST', body: { key, value } }),

  aiKeys: () => apiFetch<AiProviderKeyView[]>('/admin/ai/keys'),
  createAiKey: (body: AiProviderKeyInput) =>
    apiFetch<AiProviderKeyView>('/admin/ai/keys', { method: 'POST', body }),
  testAiKey: (id: string) =>
    apiFetch<AiKeyTestResult>(`/admin/ai/keys/${id}/test`, { method: 'POST' }),
  activateAiKey: (id: string) =>
    apiFetch<AiProviderKeyView>(`/admin/ai/keys/${id}/activate`, { method: 'POST' }),

  // Sổ kiến thức tổ chức — APM-H-i.
  knowledge: () => apiFetch<OrgKnowledgeView[]>('/admin/ai/knowledge'),
  knowledgeImpact: (tags: readonly KnowledgeTag[]) =>
    apiFetch<KnowledgeImpact>(
      `/admin/ai/knowledge/impact?tags=${encodeURIComponent(tags.join(','))}`,
    ),
  createKnowledge: (body: CreateKnowledgeInput) =>
    apiFetch<OrgKnowledgeView>('/admin/ai/knowledge', { method: 'POST', body }),
  updateKnowledge: (id: string, body: UpdateKnowledgeInput) =>
    apiFetch<OrgKnowledgeView>(`/admin/ai/knowledge/${id}`, { method: 'PATCH', body }),
  setKnowledgeStatus: (id: string, status: KnowledgeStatus) =>
    apiFetch<OrgKnowledgeView>(`/admin/ai/knowledge/${id}/status`, {
      method: 'POST',
      body: { status },
    }),
  budgets: () => apiFetch<AiBudgetView[]>('/admin/ai/budgets'),
  setBudget: (body: {
    userId?: string;
    period: 'day' | 'month';
    softLimitUsd: string;
    hardLimitUsd: string;
  }) => apiFetch<AiBudgetView[]>('/admin/ai/budgets', { method: 'POST', body }),
};

/** Đọc được bởi mọi người dùng, không chỉ admin — xem `FileCapabilities`. */
/** Bảng điều khiển tổ chức trưng bày — APM-1318c. */
export const demoActivityApi = {
  status: () => apiFetch<DemoActivityStatus>('/admin/demo-activity'),

  /**
   * Key AI **dùng chung** của tổ chức trưng bày — APM-1318e.
   *
   * `null` = chưa nhập. Đây là trạng thái bình thường, không phải lỗi: tổ chức
   * demo chạy được mọi màn hình trừ các nút gọi AI.
   */
  aiKey: () => apiFetch<{ key: AiProviderKeyView | null }>('/admin/ai/demo-key'),

  /**
   * Nhập hoặc thay key. Server làm trọn tạo → kiểm kết nối → kích hoạt, và trả
   * về **kết quả kiểm kết nối** — key sai thì không được kích hoạt, nên câu trả
   * lời ở đây là thứ quyết định giao diện báo thành công hay thất bại.
   */
  setAiKey: (body: AiProviderKeyInput) =>
    apiFetch<AiKeyTestResult>('/admin/ai/demo-key', { method: 'POST', body }),

  /**
   * Kiểm lại key **đang có** và cập nhật chuỗi model — APM-DEMO-r.
   *
   * Không cần dán lại secret: key đang hoạt động tốt, thứ thiếu là danh sách
   * tên model của gateway.
   */
  refreshAiKey: () => apiFetch<AiKeyTestResult>('/admin/ai/demo-key/refresh', { method: 'POST' }),
  /**
   * Nhật ký dựng lại + trạng thái công tắc lịch — APM-DEMO-o.
   *
   * Khoá riêng, không thăm dò 30 giây như `status`: danh sách này đổi nửa giờ
   * một lần và mỗi lượt đọc phải quét `event_log`.
   */
  resets: () => apiFetch<DemoMaintenanceStatus>('/admin/demo-activity/resets'),

  /** Kịch bản dự án demo + phần SUPER_ADMIN đang đè — APM-DEMO-p. */
  scenario: () => apiFetch<DemoScenarioView>('/admin/demo-activity/projects'),

  setProjectOverride: (scenarioKey: string, body: DemoProjectOverrideInput) =>
    apiFetch<DemoScenarioView>(`/admin/demo-activity/projects/${scenarioKey}`, {
      method: 'POST',
      body,
    }),

  clearProjectOverride: (scenarioKey: string) =>
    apiFetch<DemoScenarioView>(`/admin/demo-activity/projects/${scenarioKey}`, {
      method: 'DELETE',
    }),

  runNow: () => apiFetch<DemoActivityStatus>('/admin/demo-activity/run', { method: 'POST' }),
  resetNow: () =>
    apiFetch<{ ran: boolean; projectsDropped: number; projectsSeeded: number }>(
      '/admin/demo-activity/reset',
      { method: 'POST' },
    ),
};

export const fileApi = {
  capabilities: () => apiFetch<FileCapabilities>('/files/capabilities'),
};

export const googleOauthApi = {
  status: () => apiFetch<GoogleOauthStatus>('/admin/auth/google'),

  save: (input: GoogleOauthConfigInput) =>
    apiFetch<GoogleOauthStatus>('/admin/auth/google', { method: 'POST', body: input }),
};

export const adminKeys = {
  knowledge: ['admin', 'knowledge'] as const,
  knowledgeImpact: (tags: readonly string[]) =>
    ['admin', 'knowledge', 'impact', [...tags].sort().join(',')] as const,
  overview: ['admin', 'overview'] as const,
  aiKeys: ['admin', 'ai', 'keys'] as const,
  budgets: ['admin', 'ai', 'budgets'] as const,
  capabilities: ['files', 'capabilities'] as const,
  demoActivity: ['admin', 'demo-activity'] as const,
  demoAiKey: ['admin', 'demo-activity', 'ai-key'] as const,
  demoResets: ['admin', 'demo-activity', 'resets'] as const,
  demoProjects: ['admin', 'demo-activity', 'projects'] as const,
  googleOauth: ['admin', 'auth', 'google'] as const,
};
